import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {throwError} from 'rxjs';
import {catchError, filter, finalize, switchMap, take} from 'rxjs/operators';
import {ToastService} from '../../../services/toast/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {isDefined} from '../../utils';
import {ConfigurationService} from '../../../services/configuration/configuration.service';
import {AuthorizationService} from '../../../auth/authorization.service';
import {TokenResponse} from '@openid/appauth';
import {HttpConstants} from '../../http-constants';
import {AppConstants} from '../../app-constants';
import {AppPaths} from '../../app-paths';
import {SharedDataService} from '../../../services/shared-data/shared-data.service';

@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private isRefreshingToken = false;

  constructor(private readonly authorizationService: AuthorizationService,
              private readonly configurationService: ConfigurationService,
              private readonly sharedDataService: SharedDataService,
              private readonly toast: ToastService,
              private readonly translate: TranslateService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError(error => {
      if (error instanceof HttpErrorResponse) {
        const isTokenRequest = error.url?.includes(AppPaths.TOKEN_ENDPOINT) ?? false;
        const isCustomCredentialsRequest = error.url?.includes(AppConstants.CUSTOM_CREDENTIALS) ?? false;
        if (error.status === 401 && isCustomCredentialsRequest) {
          return throwError(() => error);
        }
        if (error.status === 401 && !isTokenRequest) {
          if (isDefined(error.error) && error.error.message === 'Your account is still blocked') {
            forkJoin([
              this.configurationService.getDefaultBankContactSettings(),
              this.configurationService.getApplicationSettings()
            ]).subscribe(([bankContactSettings, applicationSettings]) => {
              this.toast.errorWithCancelButton(
                this.translate.instant('error-message.blocked-account', {
                  supportNumber: bankContactSettings.phoneNumber,
                  appName: applicationSettings.applicationName
                })
              );
              this.authorizationService.logout();
            });
          } else if (isDefined(this.sharedDataService.getSharedData<string>(AppConstants.USER_PIN_CODE))) {
            return this.handle401Error(req, next);
          } else {
            return throwError(() => error);
          }
        }
      }
      return throwError(() => error);
    }
    ));
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      this.tokenSubject.next(null);

      const pin = this.sharedDataService.getSharedData<string>(AppConstants.USER_PIN_CODE);
      if (!isDefined(pin)) {
        this.isRefreshingToken = false;
        return throwError(() => new Error('No active session'));
      }
      return this.authorizationService.refreshToken(pin)
        .pipe(
          switchMap((newToken: TokenResponse) => {
            this.tokenSubject.next(newToken.accessToken);
            return next.handle(this.addToken(req, newToken.accessToken));
          }),
          catchError(err => {
            this.tokenSubject.error(err);
            this.tokenSubject = new BehaviorSubject<string | null>(null);
            if (this.authorizationService.isTerminalRefreshError()) {
              this.authorizationService.logout();
            }
            return throwError(() => err);
          }),
          finalize(() => this.isRefreshingToken = false)
        );
    } else {
      return this.tokenSubject
        .pipe(
          filter((token): token is string => token !== null),
          take(1),
          switchMap(token => next.handle(this.addToken(req, token)))
        );
    }
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (req.url.includes(AppPaths.TOKEN_ENDPOINT)
            || AppConstants.UNAUTHENTICATED_MARKET_RATES.test(req.url)) {
      return req;
    } else {
      return req.clone({
        setHeaders: {
          Authorization: HttpConstants.BEARER + token
        }
      });
    }
  }
}
